import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { ConfigService } from './config.service';
import {
  ApiTransaction,
  ApiTransactionStatus,
  HistoryFilter,
  PaginatedTransactions,
} from '../models/history-api.models';
import { ApiConfigService } from './api-config.service';

@Injectable({ providedIn: 'root' })
export class HistoryApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly configService: ConfigService,
  ) {}

  /** GET /api-payment{suffixe}/transaction/find/by/issuer/paged/{userId} */
  fetchByIssuerPaged(
    userId: string,
    page: number,
    size: number,
    filter?: HistoryFilter,
  ): Observable<PaginatedTransactions> {
    const url = `${this.apiConfig.transactionsByIssuerPagedUrl}/${encodeURIComponent(userId)}`;
    let params = new HttpParams().set('page', String(page)).set('size', String(size));

    if (filter?.search) {
      params = params.set('search', filter.search);
    }
    if (filter?.type) {
      params = params.set('type', filter.type);
    }
    if (filter?.status) {
      params = params.set('status', filter.status);
    }
    if (filter?.startDate) {
      params = params.set('startDate', filter.startDate);
    }
    if (filter?.endDate) {
      params = params.set('endDate', filter.endDate);
    }

    return this.http.get<unknown>(url, { params, observe: 'response' }).pipe(
      map((response) => {
        if (response.status === 204 || response.body == null) {
          return this.emptyPage(page, size);
        }
        return this.parsePaginated(response.body, page, size);
      }),
      tap((result) => {
        if (this.configService.enableApiLogs) {
          console.log('[HistoryApi] paged', url, result);
        }
      }),
    );
  }

  private emptyPage(page: number, size: number): PaginatedTransactions {
    return {
      transactions: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: page,
      pageSize: size,
      isLast: true,
      isFirst: true,
      isEmpty: true,
    };
  }

  private parsePaginated(data: unknown, page: number, size: number): PaginatedTransactions {
    if (!data || typeof data !== 'object') {
      return this.emptyPage(page, size);
    }

    const raw = data as Record<string, unknown>;
    const content = raw['content'];

    if (Array.isArray(content)) {
      const transactions = content.map((item) => this.parseTransaction(item));
      return {
        transactions,
        totalElements: this.toNumber(raw['totalElements'], transactions.length),
        totalPages: this.toNumber(raw['totalPages'], 1),
        currentPage: this.toNumber(raw['number'], page),
        pageSize: this.toNumber(raw['size'], size),
        isLast:
          raw['last'] === true ||
          this.toNumber(raw['totalPages'], 1) <= this.toNumber(raw['number'], page) + 1,
        isFirst: raw['first'] !== false,
        isEmpty: raw['empty'] === true || transactions.length === 0,
      };
    }

    const transactions = this.parseTransactionMap(raw);
    return {
      transactions,
      totalElements: transactions.length,
      totalPages: 1,
      currentPage: 0,
      pageSize: transactions.length,
      isLast: true,
      isFirst: true,
      isEmpty: transactions.length === 0,
    };
  }

  private parseTransactionMap(raw: Record<string, unknown>): ApiTransaction[] {
    const all: ApiTransaction[] = [];
    Object.values(raw).forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach((item) => all.push(this.parseTransaction(item)));
      }
    });
    return all;
  }

  private parseTransaction(item: unknown): ApiTransaction {
    const raw = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    const currency = this.asRecord(raw['currency']);
    const provider = this.asRecord(raw['provider']);
    const statuses = Array.isArray(raw['transactionStatus']) ? raw['transactionStatus'] : [];

    return {
      id: String(raw['id'] ?? ''),
      amount: this.toNumber(raw['amount'], 0),
      accountNumber: this.toOptionalString(raw['accountNumber']),
      issuerId: this.toOptionalString(raw['issuerId']),
      projectNumber: this.toOptionalString(raw['projectNumber']),
      contractNumber: this.toOptionalString(raw['contractNumber']),
      reference: this.toOptionalString(raw['reference']),
      description: this.toOptionalString(raw['description']),
      subscriptionValidated: typeof raw['subscriptionValidated'] === 'boolean'
        ? raw['subscriptionValidated']
        : null,
      productId: this.toOptionalString(raw['productId']),
      productName: this.toOptionalString(raw['productName']),
      currency: {
        id: this.toOptionalString(currency['id']),
        shortCode: this.toOptionalString(currency['shortCode']),
        longCode: this.toOptionalString(currency['longCode']),
      },
      provider: {
        id: this.toOptionalString(provider['id']),
        name: this.toOptionalString(provider['name']),
        payType: this.toOptionalString(provider['payType']),
        imageId: this.toOptionalString(provider['imageId']),
        description: this.toOptionalString(provider['description']),
      },
      transactionStatus: statuses.map((status) => this.parseStatus(status)),
      createdAt: this.toOptionalString(raw['createdAt']),
      updatedAt: this.toOptionalString(raw['updatedAt']),
    };
  }

  private parseStatus(item: unknown): ApiTransactionStatus {
    const raw = this.asRecord(item);
    const status = this.asRecord(raw['status']);
    return {
      id: this.toOptionalString(raw['id']),
      status: {
        id: this.toOptionalString(status['id']),
        code: this.toOptionalString(status['code']),
        label: this.toOptionalString(status['label']),
        colorCode: this.toOptionalString(status['colorCode']),
      },
      createdAt: this.toOptionalString(raw['createdAt']),
      updatedAt: this.toOptionalString(raw['updatedAt'] ?? raw['updateAt']),
      updateAt: this.toOptionalString(raw['updateAt']),
    };
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private toOptionalString(value: unknown): string | undefined {
    if (value == null) {
      return undefined;
    }
    const text = String(value).trim();
    return text ? text : undefined;
  }

  private toNumber(value: unknown, fallback: number): number {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  }
}
